import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Modal,
    ModalBody,
    ModalHeader,
    Table,
} from "reactstrap";
import Loaders from "components/Common/Loaders";
import { fetchCandidateOnboardingDocumentsById } from "model/CandidateOnboardingModel";

const getInlineViewUrl = (url = "") => {
    const raw = String(url ?? "").trim();
    if (!raw) return "";
    if (/^data:|^blob:/i.test(raw)) return raw;
    if (/\/inline\/?$/i.test(raw)) return raw.replace(/\/$/, "");
    return `${raw.replace(/\/$/, "")}/inline`;
};

const normalizeDocumentRows = (payload = {}) => {
    const root = payload?.data ?? payload ?? {};
    const list = Array.isArray(root?.documents)
        ? root.documents
        : Array.isArray(root?.records)
            ? root.records
            : Array.isArray(root?.data)
                ? root.data
                : Array.isArray(root)
                    ? root
                    : [];

    return (Array.isArray(list) ? list : []).map((item = {}) => ({
        onboardingDocumentId:
            item?.onboardingDocumentId ??
            item?.onboarding_document_id ??
            item?.id ??
            null,
        documentName:
            item?.documentName ??
            item?.document_name ??
            item?.name ??
            item?.title ??
            "",
        documentNumber:
            item?.documentNumber ??
            item?.document_number ??
            item?.documentNo ??
            item?.document_no ??
            item?.number ??
            "",
        status: item?.status ?? item?.statusName ?? "",
        uploadUrl:
            item?.uploadUrl ??
            item?.upload_url ??
            item?.fileUrl ??
            item?.file_url ??
            "",
    }));
};

const getDocStatusBadgeClass = (status = "") => {
    const normalized = String(status).trim().toLowerCase();
    if (normalized === "pending") {
        return "candidate-onboarding-doc-status-badge candidate-onboarding-doc-status-badge--pending";
    }
    return "candidate-onboarding-doc-status-badge";
};

const CandidateOnboardingViewModal = ({
    isOpen = false,
    toggle,
    selectedRow = null,
}) => {
    const [loading, setLoading] = useState(false);
    const [documentRows, setDocumentRows] = useState([]);
    const [summary, setSummary] = useState({
        candidate: "",
        facility: "",
    });

    useEffect(() => {
        if (!isOpen) {
            setDocumentRows([]);
            setSummary({ candidate: "", facility: "" });
            return;
        }

        const loadDocuments = async () => {
            const rowId =
                selectedRow?.id ??
                selectedRow?._id ??
                selectedRow?.candidateOnboardingId ??
                "";
            if (!rowId) {
                setDocumentRows([]);
                return;
            }

            setLoading(true);
            const response = await fetchCandidateOnboardingDocumentsById(rowId);
            const responseData = response?.data ?? response ?? {};

            setSummary({
                candidate:
                    responseData?.candidate ??
                    responseData?.candidateName ??
                    selectedRow?.candidate ??
                    selectedRow?.candidateName ??
                    selectedRow?.name ??
                    "",
                facility:
                    responseData?.facilityDisplay ??
                    responseData?.facility ??
                    responseData?.facilityName ??
                    responseData?.client ??
                    selectedRow?.facility ??
                    selectedRow?.client ??
                    selectedRow?.clientName ??
                    "",
            });
            setDocumentRows(normalizeDocumentRows(responseData));
            setLoading(false);
        };

        loadDocuments();
    }, [isOpen, selectedRow]);

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            backdrop="static"
            centered
            size="lg"
            zIndex={5000}
            className="modal-full-height candidate-onboarding-view-modal"
        >
            <ModalHeader toggle={toggle} className="fw-bold">
                TSA PreCheck Documents
            </ModalHeader>
            <ModalBody>
                <div className="candidate-onboarding-view-summary mb-3">
                    <div>
                        <span className="fw-semibold">Candidate:</span>{" "}
                        {summary.candidate || "—"}
                    </div>
                    <div className="mt-1">
                        <span className="fw-semibold">Facility:</span>{" "}
                        {summary.facility || "—"}
                    </div>
                </div>

                {loading ? (
                    <Loaders />
                ) : (
                    <div className="candidate-onboarding-documents-wrap">
                        <Table className="align-middle mb-0 candidate-onboarding-documents-table">
                            <thead>
                                <tr>
                                    <th>Document Name</th>
                                    <th>Document #</th>
                                    <th>Status</th>
                                    <th className="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {documentRows.length ? (
                                    documentRows.map((row, index) => (
                                        <tr
                                            key={
                                                row.onboardingDocumentId != null
                                                    ? `onboarding-doc-${row.onboardingDocumentId}`
                                                    : `onboarding-doc-${index}`
                                            }
                                        >
                                            <td>{row.documentName || "—"}</td>
                                            <td>{row.documentNumber || "—"}</td>
                                            <td>
                                                {row.status ? (
                                                    <span className={getDocStatusBadgeClass(row.status)}>
                                                        {row.status}
                                                    </span>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                            <td className="text-end">
                                                {row.uploadUrl ? (
                                                    <a
                                                        href={getInlineViewUrl(row.uploadUrl)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="candidate-onboarding-view-doc-btn d-inline-flex align-items-center justify-content-center"
                                                        title="View document"
                                                        aria-label="View document"
                                                    >
                                                        <i
                                                            className="mdi mdi-eye-outline font-size-18 text-primary"
                                                            aria-hidden="true"
                                                        />
                                                    </a>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted py-4">
                                            No documents found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

CandidateOnboardingViewModal.propTypes = {
    isOpen: PropTypes.bool,
    toggle: PropTypes.func,
    selectedRow: PropTypes.object,
};

export default CandidateOnboardingViewModal;
